/**
 * StockSearchDropdown — reusable searchable stock picker.
 *
 * Issue #7 / #9 — used by Compare, AI Insights, Watchlist add, Alert
 * create, and anywhere a single CSE security needs to be selected.
 *
 * Props:
 *   value        : string  — currently selected cse_symbol (or "")
 *   onChange     : fn(cse_symbol, security) — called when the user picks one
 *   securities   : Array<{cse_symbol, company_name, share_type, sector?}>
 *                  Optional. If omitted, the component fetches /securities.
 *   excluded     : string[] — cse_symbols to hide/grey-out (e.g. already added)
 *   placeholder  : string   — trigger placeholder when nothing is selected
 *   triggerClassName : string — override trigger button className
 *   testId       : string   — base data-testid for trigger + options
 *   groupBySector: boolean  — default true
 *   autoOpen     : boolean  — open on mount
 *   size         : "sm"|"md" — trigger height
 *   clearable    : boolean  — show a small "Clear" item
 */
import React, { useEffect, useMemo, useState } from "react";
import { apiGet } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";

export const StockSearchDropdown = ({
  value = "",
  onChange,
  securities = null,
  excluded = [],
  placeholder = "Search ticker or company…",
  triggerClassName = "",
  testId = "stock-search",
  groupBySector = true,
  autoOpen = false,
  size = "md",
  clearable = false,
}) => {
  const [open, setOpen] = useState(!!autoOpen);
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    if (securities) return;
    let active = true;
    apiGet("/securities")
      .then((rows) => {
        if (!active) return;
        setFetched(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (active) setFetched([]);
      });
    return () => {
      active = false;
    };
  }, [securities]);

  const list = securities || fetched || [];
  const excludedSet = useMemo(() => new Set(excluded || []), [excluded]);

  const selected = useMemo(
    () => (list || []).find((s) => s.cse_symbol === value) || null,
    [list, value]
  );

  const groups = useMemo(() => {
    if (!groupBySector) {
      return [
        {
          sector: "All",
          items: [...(list || [])].sort((a, b) =>
            (a.cse_symbol || "").localeCompare(b.cse_symbol || "")
          ),
        },
      ];
    }
    const buckets = {};
    for (const s of list || []) {
      const sector = s.sector || "Other";
      if (!buckets[sector]) buckets[sector] = [];
      buckets[sector].push(s);
    }
    return Object.entries(buckets)
      .map(([sector, items]) => ({
        sector,
        items: items.sort((a, b) => (a.cse_symbol || "").localeCompare(b.cse_symbol || "")),
      }))
      .sort((a, b) => a.sector.localeCompare(b.sector));
  }, [list, groupBySector]);

  const triggerHeight = size === "sm" ? "h-9" : "h-10";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between font-normal ${triggerHeight} ${triggerClassName}`}
          data-testid={`${testId}-trigger`}
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0 truncate">
              <span className="font-mono text-[11px] text-muted-foreground shrink-0">
                {selected.cse_symbol}
              </span>
              <span className="truncate text-sm">
                — {selected.company_name || selected.display_name}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground inline-flex items-center gap-2">
              <Search className="h-3.5 w-3.5" /> {placeholder}
            </span>
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[320px]"
        align="start"
        data-testid={`${testId}-popover`}
      >
        <Command shouldFilter>
          <CommandInput
            placeholder={placeholder}
            data-testid={`${testId}-input`}
          />
          <CommandList className="max-h-72">
            <CommandEmpty>No matches.</CommandEmpty>
            {clearable && value && (
              <CommandGroup>
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onChange?.("", null);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-clear`}
                >
                  <X className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Clear selection</span>
                </CommandItem>
              </CommandGroup>
            )}
            {groups.map((g) => (
              <CommandGroup key={g.sector} heading={groupBySector ? `${g.sector} (${g.items.length})` : undefined}>
                {g.items.map((s) => {
                  const disabled = excludedSet.has(s.cse_symbol);
                  const isSelected = s.cse_symbol === value;
                  const needle = `${s.cse_symbol} ${s.company_name || s.display_name || ""} ${g.sector}`;
                  return (
                    <CommandItem
                      key={s.cse_symbol}
                      value={needle}
                      disabled={disabled}
                      onSelect={() => {
                        if (disabled) return;
                        onChange?.(s.cse_symbol, s);
                        setOpen(false);
                      }}
                      data-testid={`${testId}-option-${s.cse_symbol}`}
                    >
                      <Check
                        className={`h-3.5 w-3.5 mr-2 ${isSelected ? "opacity-100 text-primary" : "opacity-0"}`}
                      />
                      <span className="font-mono text-[11px] text-muted-foreground w-20 shrink-0">
                        {s.cse_symbol}
                      </span>
                      <span className="truncate text-sm">
                        — {s.company_name || s.display_name}
                      </span>
                      {disabled && (
                        <span className="ml-auto text-[10px] text-muted-foreground">already added</span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default StockSearchDropdown;
